import React, { useEffect, useState } from 'react'
import { Card, Button, Alert, Typography, Spin, Progress, Space, message } from 'antd'
import { DownloadOutlined, SyncOutlined } from '@ant-design/icons'
import api, { backend } from '../../api'

const { Title, Paragraph, Text } = Typography

const CURRENT_VERSION = 'v3.0.0'

export default function AutoUpdate() {
  const [checking, setChecking] = useState(false)
  const [updateInfo, setUpdateInfo] = useState(null)
  const [noUpdate, setNoUpdate] = useState(false)
  const [error, setError] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [installerPath, setInstallerPath] = useState('')

  useEffect(() => {
    return api.on('update:download-progress', info => {
      setDownloadProgress(info.percent || 0)
    })
  }, [])

  const handleCheckUpdate = async () => {
    setChecking(true)
    setUpdateInfo(null)
    setNoUpdate(false)
    setError('')
    try {
      const res = await backend.checkVersion('3.0.0')
      if (res.has_update) {
        setUpdateInfo(res)
      } else {
        setNoUpdate(true)
      }
    } catch (err) {
      setError('检查更新失败: ' + (err.message || '未知错误'))
    } finally {
      setChecking(false)
    }
  }

  const handleDownload = async () => {
    if (!updateInfo?.download_url) return message.warning('暂无更新包下载地址')
    setDownloading(true)
    setDownloadProgress(0)
    setInstallerPath('')
    setError('')
    try {
      const res = await api.downloadUpdate(updateInfo)
      if (res.success) {
        setInstallerPath(res.path)
        message.success('更新包下载完成')
      } else {
        setError(res.message || '下载失败')
      }
    } catch (err) {
      setError('下载失败: ' + (err.message || '未知错误'))
    } finally {
      setDownloading(false)
    }
  }

  const handleOpenInstaller = async () => {
    const res = await api.openUpdateInstaller(installerPath)
    if (!res.success) message.error(res.message || '打开安装包失败')
  }

  return (
    <div>
      <Title level={4}>自动更新</Title>
      <Card style={{ maxWidth: 600 }}>
        <div style={{ marginBottom: 16 }}>
          <Text strong>当前版本: </Text>
          <Text>{CURRENT_VERSION}</Text>
        </div>

        <Button
          type="primary"
          icon={<SyncOutlined />}
          loading={checking}
          onClick={handleCheckUpdate}
        >
          检查更新
        </Button>

        {checking && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Spin tip="正在检查更新..." />
          </div>
        )}

        {noUpdate && (
          <Alert
            message="已是最新版本"
            description="当前版本已是最新，无需更新"
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {updateInfo && (
          <Alert
            message={`发现新版本: ${updateInfo.version || ''}`}
            description={
              <div>
                {updateInfo.changelog && (
                  <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                    {updateInfo.changelog}
                  </Paragraph>
                )}
                {updateInfo.file_size ? (
                  <Paragraph style={{ marginTop: 8 }}>大小: {(updateInfo.file_size / 1024 / 1024).toFixed(2)} MB</Paragraph>
                ) : null}
                {updateInfo.download_url && (
                  <Paragraph style={{ marginTop: 8 }}>
                    下载地址: {updateInfo.download_url}
                  </Paragraph>
                )}
                <Space style={{ marginTop: 8 }}>
                  <Button type="primary" icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>下载安装包</Button>
                  {installerPath && <Button onClick={handleOpenInstaller}>打开安装包</Button>}
                </Space>
                {downloading && <Progress percent={downloadProgress} style={{ marginTop: 12 }} />}
                {updateInfo.sha256 && <Paragraph style={{ marginTop: 8 }}><Text type="secondary">SHA256: {updateInfo.sha256}</Text></Paragraph>}
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {error && (
          <Alert
            message="检查失败"
            description={error}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </div>
  )
}
